import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  ListItem,
  List,
  ListItemText,
  MenuItem,
  Checkbox,
  Alert,
  Card,
  CardContent,
  Grid,
  Collapse,
  Tooltip,
  TableFooter,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Search,
  Close,
  Person,
  Payment,
  Receipt,
  AccountBalance,
  CheckCircle,
  Warning,
  ExpandMore,
  ExpandLess,
  ShoppingCart,
  Edit,
  Add,
  Delete,
  Save,
  NavigateNext,
} from "@mui/icons-material";
import { formatCurrencyN } from "../../../common/utils_t";
import {
  createCustomerPayment,
  getCustomerTransactions,
  searchCustomers,
  getAllCustomers2,
  getCustomerTransactionsWithDate,
  getCustomerTransactionsWithDate_Next,
  // Add these API functions:
  // getCustomerReport,        // For initial report generation with dates
  // getNextCustomerReport,    // For pagination
} from "../../../api/customers/customers";
import { IonApp, IonContent, IonHeader, IonPage, IonToolbar } from "@ionic/react";
import ReceiptModal from '../../../components/sales/ReceiptModal';
import { MenuButton } from "../../../components/Menu";

interface OrderItem {
  id?: string;
  product_id: string;
  quantity: string;
  product_name: string;
  price: string;
  total_amount: string;
  item_status: string;
  description?: string;
}

interface Transaction {
  order_id: string;
  order_code: string;
  order_date: string;
  order_status: "COMPLETED" | "PENDING" | "CANCELLED";
  payment_status: '0' | '1' | '2';
  total_price: string;
  member_id: string;
  member_name: string;
  mode_of_payment: string;
  order_by: string;
  otp_status: string | null;
  items: OrderItem[];
  balance_due?: number;
  payable_amount?: string | null;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  business_id?: string;
  branch_id?: string;
  amount_paid?: number;
}

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  grade_letter: string;
  user_id: string;
}

interface EditableOrder {
  order_id: string;
  order_code: string;
  order_date: string;
  order_status: "COMPLETED" | "PENDING" | "CANCELLED";
  payment_status: '0' | '1' | '2';
  total_price: string;
  mode_of_payment: string;
  payable_amount: string;
  balance_due: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  order_by: string;
  approved_by: string;
  member_id: string;
  business_id: string;
  branch_id: string;
  amount_paid: string;
  active_status: string;
}

interface EditableOrderItem {
  id: string;
  order_id: string;
  product_id: string;
  quantity: string;
  price: string;
  total_amount: string;
  status: string;
  description: string;
  product_name: string;
  created_at: string;
  updated_at: string;
  branch_id: string;
  business_id: string;
}

interface EditableOrderReceipt {
  id: string;
  order_id: string;
  order_code: string;
  total_price: string;
  amount_paid: string;
  balance_due: string;
  payment_status: '0' | '1' | '2';
  mode_of_payment: string;
  order_date: string;
  business_id: string;
  branch_id: string;
  order_by: string;
  approved_by: string;
  member_id: string;
  active_status: string;
  is_verified: string;
  is_audited: string;
}

const CustomerAccountPage_account: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchedCustomers, setSearchedCustomers] = useState<Customer[]>([]);
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState({
    customers: false,
    transactions: false,
    payment: false,
    report: false,
    nextReport: false,
  });

  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>([]);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Transaction | null>(null);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editableOrder, setEditableOrder] = useState<EditableOrder | null>(null);
  const [editableOrderItems, setEditableOrderItems] = useState<EditableOrderItem[]>([]);
  const [editableOrderReceipt, setEditableOrderReceipt] = useState<EditableOrderReceipt | null>(null);

  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState<any>(null);

  // NEW STATE VARIABLES FOR REPORT GENERATION
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [reportGenerated, setReportGenerated] = useState(false);



  const [nextPageTokenx , setNextPageTokenx] = useState<string | null>(null);
  // Helper functions
  const getPaymentStatusLabel = (status: string | number) => {
    const code = Number(status);
    switch (code) {
      case 0:
        return "Unpaid";
      case 1:
        return "Paid";
      case 2:
        return "Partial";
      default:
        return "Unknown";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case "0":
        return "error";
      case "1":
        return "success";
      case "2":
        return "warning";
      default:
        return "default";
    }
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "success";
      case "PENDING":
        return "warning";
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const toggleRowExpansion = (orderId: string) => {
    setExpandedRows((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  // MODIFIED: Updated to include date parameters
  const fetchCustomerReport = async (customerId: string, startDate: string, endDate: string, pageToken: string | null = null) => {
    try {
      setLoading((prev) => ({ ...prev, transactions: true, report: pageToken === null }));
      
      // This is where you'll call your new API endpoint
      // Example API call structure:
      // const response = await getCustomerReport({
      //   customerId,
      //   startDate,
      //   endDate,
      //   pageToken,
      // });
      
      // For now, using the existing function but you should replace it with the new endpoint
      const response = await getCustomerTransactionsWithDate(customerId, startDate, endDate);
      setNextPageTokenx(response.data.orders.length || 0);
      const processedTransactions = response.data.orders.map((txn: Transaction) => {
        const totalAmount = parseFloat(txn.payable_amount || txn.total_price);
        let balanceDue = Number(txn.balance_due);

        if (txn.payment_status === "2") {
          balanceDue = totalAmount * 0.5;
        } else if (txn.payment_status === "1") {
          balanceDue = 0;
        }

        return {
          ...txn,
          balance_due: balanceDue,
        };
      });
      
      if (pageToken === null) {
        // First page
        setTransactions(processedTransactions);
      } else {
        // Append to existing transactions
        setTransactions(prev => [...prev, ...processedTransactions]);
      }
      
      setBalance(Number(response.data.balance));
      
      // Assuming your API returns a nextPageToken when there's more data
      // Replace 'response.data.nextPageToken' with the actual field from your API
      const nextToken = response.data.nextPageToken || null;
      setNextPageToken(nextToken);
      
      if (pageToken === null) {
        setCurrentPage(1);
        setReportGenerated(true);
      } else {
        setCurrentPage(prev => prev + 1);
      }

    } catch (error) {
      console.error("Error fetching report:", error);
      alert("Failed to fetch report");
    } finally {
      setLoading((prev) => ({ 
        ...prev, 
        transactions: false, 
        report: false,
        nextReport: false 
      }));
    }
  };



   const fetchCustomerReport_Next = async (customerId: string, startDate: string, endDate: string, pageToken: string | null = null) => {
    try {
      setLoading((prev) => ({ ...prev, transactions: true, report: pageToken === null }));
      
      // This is where you'll call your new API endpoint
      // Example API call structure:
      // const response = await getCustomerReport({
      //   customerId,
      //   startDate,
      //   endDate,
      //   pageToken,
      // });
      
      // For now, using the existing function but you should replace it with the new endpoint
      const response = await getCustomerTransactionsWithDate_Next(customerId,startDate,endDate,nextPageTokenx);
      
      const processedTransactions = response.data.orders.map((txn: Transaction) => {
        const totalAmount = parseFloat(txn.payable_amount || txn.total_price);
        let balanceDue = Number(txn.balance_due);

        setNextPageTokenx(response.data.orders.length + nextPageTokenx);
        if (txn.payment_status === "2") {
          balanceDue = totalAmount * 0.5;
        } else if (txn.payment_status === "1") {
          balanceDue = 0;
        }

        return {
          ...txn,
          balance_due: balanceDue,
        };
      });
      
      if (pageToken === null) {
        // First page
        setTransactions(processedTransactions);
      } else {
        // Append to existing transactions
        setTransactions(prev => [...prev, ...processedTransactions]);
      }
      
      setBalance(Number(response.data.balance));
      
      // Assuming your API returns a nextPageToken when there's more data
      // Replace 'response.data.nextPageToken' with the actual field from your API
      const nextToken = response.data.nextPageToken || null;
      setNextPageToken(nextToken);
      
      if (pageToken === null) {
        setCurrentPage(1);
        setReportGenerated(true);
      } else {
        setCurrentPage(prev => prev + 1);
      }

    } catch (error) {
      console.error("Error fetching report:", error);
      alert("Failed to fetch report");
    } finally {
      setLoading((prev) => ({ 
        ...prev, 
        transactions: false, 
        report: false,
        nextReport: false 
      }));
    }
  };
  // NEW: Handle Next Report Page
  const handleNextReport = async () => {
    if (!selectedCustomer || !startDate || !endDate || !nextPageTokenx) return;

    try {
      setLoading(prev => ({ ...prev, nextReport: true }));
      
      // Call your next report endpoint
      // Example:
      // const response = await getNextCustomerReport({
      //   customerId: selectedCustomer.user_id,
      //   startDate,
      //   endDate,
      //   pageToken: nextPageToken,
      // });
      
      // For now, using a placeholder
      await fetchCustomerReport_Next(selectedCustomer.user_id, startDate, endDate, nextPageTokenx);
      
    } catch (error) {
      console.error("Error fetching next report:", error);
      alert("Failed to load next page");
    }
  };

  // MODIFIED: Don't auto-fetch transactions on customer select
  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchQuery("");
    setSearchedCustomers([]);
    setSelectedTransactions([]);
    setPaymentAmount(0);
    setExpandedRows([]);
    // Reset report state
    setTransactions([]);
    setBalance(0);
    setStartDate("");
    setEndDate("");
    setNextPageToken(null);
    setCurrentPage(0);
    setReportGenerated(false);
  };

  const handleTransactionSelect = (transactionId: string) => {
    setSelectedTransactions((prev) => {
      const newSelected = prev.includes(transactionId)
        ? prev.filter((id) => id !== transactionId)
        : [...prev, transactionId];

      const unpaidTransactions = transactions.filter(
        (txn) => txn.payment_status === '0' || txn.payment_status === "2"
      );

      const newTotal = unpaidTransactions
        .filter((txn) => newSelected.includes(txn.order_id))
        .reduce(
          (sum, txn) => sum + (txn.balance_due || parseFloat(txn.total_price)),
          0
        );

      setPaymentAmount(newTotal);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    const unpaidTransactions = transactions.filter(
      (txn) => txn.payment_status === '0' || txn.payment_status === "2"
    );

    if (selectAll) {
      setSelectedTransactions([]);
      setPaymentAmount(0);
    } else {
      const allUnpaidIds = unpaidTransactions.map((txn) => txn.order_id);
      setSelectedTransactions(allUnpaidIds);
      
      const totalOutstanding = unpaidTransactions.reduce((sum, txn) => {
        const amount = parseFloat(txn.total_price);
        const balanceDue = txn.balance_due || amount;
        return sum + balanceDue;
      }, 0);
      
      setPaymentAmount(totalOutstanding);
    }
    setSelectAll(!selectAll);
  };

  const handlePaymentAmountChange = (value: number) => {
    setPaymentAmount(Math.abs(value) || 0);
  };

  const getPaymentType = () => {
    const unpaidTransactions = transactions.filter(
      (txn) => txn.payment_status === '0' || txn.payment_status === "2"
    );

    const selectedTransactionTotal = unpaidTransactions
      .filter((txn) => selectedTransactions.includes(txn.order_id))
      .reduce((sum, txn) => {
        const amount = txn.total_price || txn.payable_amount || "0";
        const balanceDue = txn.balance_due || amount;
        return sum + Number(balanceDue);
      }, 0);

    if (paymentAmount === selectedTransactionTotal) return "exact";
    if (paymentAmount > selectedTransactionTotal) return "overpayment";
    if (paymentAmount < selectedTransactionTotal) return "partial";
    return "exact";
  };

  const handlePaymentSubmit = async () => {
    if (!selectedCustomer || selectedTransactions.length === 0 || paymentAmount <= 0) return;

    try {
      setLoading((prev) => ({ ...prev, payment: true }));
      const paymentType = getPaymentType();

      const data = {
        customer_id: selectedCustomer.user_id,
        order_ids: selectedTransactions,
        amount: paymentAmount,
        payment_method: paymentMethod,
        payment_type: paymentType,
        notes: paymentNotes || `Payment for ${selectedTransactions.length} order(s)`,
      };

      const response = await createCustomerPayment(data);
      handleGenerateReceipt(data);
      
      // Refresh the report after payment
      if (reportGenerated && startDate && endDate) {
        await fetchCustomerReport(selectedCustomer.user_id, startDate, endDate, null);
      }

      setShowPaymentDialog(false);
      setSelectedTransactions([]);
      setPaymentAmount(0);
      setPaymentNotes("");
      setSelectAll(false);
    } catch (error) {
      console.error("Error creating payment:", error);
    } finally {
      setLoading((prev) => ({ ...prev, payment: false }));
    }
  };

  const handleSearchChange = async (searchTerm: string) => {
    if (searchTerm.length < 3) {
      setSearchedCustomers([]);
      return;
    }
    try {
      setLoading((prev) => ({ ...prev, customers: true }));
      const response = await searchCustomers(searchTerm);
      setSearchedCustomers(response.data);
    } catch (error) {
      console.error("Error searching customers:", error);
    } finally {
      setLoading((prev) => ({ ...prev, customers: false }));
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchedCustomers([]);
      return;
    }
    const timerId = setTimeout(() => {
      handleSearchChange(searchQuery);
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchQuery]);

  const fetchAllCustomers = async () => {
    try {
      setLoading((prev) => ({ ...prev, customers: true }));
      const response = await getAllCustomers2();
      setAllCustomers(response.data);
    } catch (error) {
      console.error("Error fetching all customers:", error);
    } finally {
      setLoading((prev) => ({ ...prev, customers: false }));
    }
  };

  useEffect(() => {
    const unpaidTransactions = transactions.filter(
      (txn) => txn.payment_status === '0' || txn.payment_status === "2"
    );

    setSelectAll(
      selectedTransactions.length === unpaidTransactions.length &&
        unpaidTransactions.length > 0
    );
  }, [selectedTransactions, transactions]);

  const handleGenerateReceipt = (paymentData: any) => {
    if (!selectedCustomer) return;

    const paidTransactions = transactions.filter(txn => 
      paymentData.order_ids.includes(txn.order_id)
    );

    const totalAmount = paidTransactions.reduce((sum, txn) => {
      return sum + txn.items.reduce((txnSum, item) => {
        return txnSum + (parseFloat(item.total_amount) || 0);
      }, 0);
    }, 0);

    const initialBalanceDue = paidTransactions.reduce((sum, txn) => {
      return sum + (txn.balance_due || 0);
    }, 0);

    const paidAmount = paymentData.amount;
    
    const actualBalanceDue = Math.max(0, initialBalanceDue - paidAmount);

    const receiptItems = paidTransactions.flatMap(txn => 
      txn.items.map((item, index) => ({
        orderID: `Item ${index + 1}`,
        productName: item.product_name || 'Unknown Product',
        quantity: item.quantity || '1',
        unitPrice: parseFloat(item.price) || 0,
        amount: parseFloat(item.total_amount) || 0,
        status: item.item_status || 'COMPLETED'
      }))
    );

    setReceiptData({
      customer: {
        name: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
        phone: selectedCustomer.phone || 'N/A',
        email: selectedCustomer.email || 'N/A'
      },
      order: {
        id: paidTransactions.length === 1 
          ? paidTransactions[0].order_code 
          : `Multiple Orders (${paidTransactions.length})`,
        date: paidTransactions.length === 1 
          ? paidTransactions[0].order_date 
          : new Date().toISOString(),
        status: paidTransactions.length === 1 
          ? paidTransactions[0].order_status 
          : 'COMPLETED',
        paymentStatus: '1',
        paymentMethod: paymentData.payment_method || 'cash'
      },
      items: receiptItems,
      summary: {
        totalAmount,
        paidAmount,
        balanceDue: actualBalanceDue
      },
      payment: {
        amount: paidAmount,
        method: paymentData.payment_method || 'cash',
        date: new Date().toISOString(),
        notes: paymentData.notes || `Payment for ${paidTransactions.length} order(s)`
      }
    });
    setReceiptOpen(true);
  };

  const handlePrintReceipt = (transaction: Transaction) => {
    if (!selectedCustomer) return;

    const receiptItems = transaction.items.map((item, index) => ({
      orderID: `Item ${index + 1}`,
      productName: item.product_name,
      quantity: item.quantity,
      unitPrice: parseFloat(item.price),
      amount: parseFloat(item.total_amount),
      status: item.item_status
    }));

    const totalAmount = parseFloat(transaction.payable_amount || transaction.total_price);
    const paidAmount = totalAmount - (transaction.balance_due || 0);

    setReceiptData({
      customer: {
        name: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
        phone: selectedCustomer.phone,
        email: selectedCustomer.email
      },
      order: {
        id: transaction.order_code,
        date: transaction.order_date,
        status: transaction.order_status,
        paymentStatus: getPaymentStatusLabel(transaction.payment_status),
        paymentMethod: transaction.mode_of_payment || 'cash'
      },
      items: receiptItems,
      summary: {
        totalAmount,
        paidAmount,
        balanceDue: transaction.balance_due || 0
      }
    });
    setReceiptOpen(true);
  };

  // EDIT ORDER FUNCTIONS (keep as is)
  const handleEditOrder = async (transaction: Transaction) => {
    setEditingOrder(transaction);
    setLoadingOrderDetails(true);
    
    try {
      const orderData = {
        order: {
          order_id: transaction.order_id,
          order_code: transaction.order_code,
          order_date: transaction.order_date,
          total_price: transaction.total_price,
          payment_status: transaction.payment_status,
          mode_of_payment: transaction.mode_of_payment,
          order_status: transaction.order_status,
          payable_amount: transaction.payable_amount || transaction.total_price,
          balance_due: (transaction.balance_due || 0).toString(),
          customer_name: transaction.customer_name || `${selectedCustomer?.firstName} ${selectedCustomer?.lastName}`,
          customer_email: transaction.customer_email || selectedCustomer?.email,
          customer_phone: transaction.customer_phone || selectedCustomer?.phone,
          order_by: transaction.order_by,
          approved_by: "",
          member_id: transaction.member_id,
          business_id: transaction.business_id || "",
          branch_id: transaction.branch_id || "",
          amount_paid: (parseFloat(transaction.total_price) - (transaction.balance_due || 0)).toString(),
          active_status: "ACTIVE"
        },
        order_items: transaction.items.map((item, index) => ({
          id: `item-${index}`,
          order_id: transaction.order_id,
          product_id: item.product_id,
          product_name: item.product_name,
          description: item.description || `Product: ${item.product_name}`,
          quantity: item.quantity,
          price: item.price,
          total_amount: item.total_amount,
          status: item.item_status,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          branch_id: transaction.branch_id || "",
          business_id: transaction.business_id || "",
        })),
        order_receipt: {
          id: `receipt-${transaction.order_id}`,
          order_id: transaction.order_id,
          order_code: `RPT-${transaction.order_code}`,
          total_price: transaction.total_price,
          amount_paid: (parseFloat(transaction.total_price) - (transaction.balance_due || 0)).toString(),
          balance_due: (transaction.balance_due || 0).toString(),
          payment_status: transaction.payment_status,
          mode_of_payment: transaction.mode_of_payment,
          order_date: transaction.order_date,
          business_id: transaction.business_id || "",
          branch_id: transaction.branch_id || "",
          order_by: transaction.order_by,
          approved_by: "",
          member_id: transaction.member_id,
          active_status: "COMPLETED",
          is_verified: "0",
          is_audited: "0"
        }
      };

      setOrderDetails(orderData);
      setEditableOrder(orderData.order);
      setEditableOrderItems(orderData.order_items);
      setEditableOrderReceipt(orderData.order_receipt);
      setEditDialogOpen(true);
      
    } catch (error) {
      console.error('Error preparing order details:', error);
      alert('Error loading order details');
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const handleOrderFieldChange = useCallback((field: keyof EditableOrder, value: string) => {
    setEditableOrder(prev => prev ? { ...prev, [field]: value } : null);
  }, []);

  const handleOrderItemFieldChange = useCallback((index: number, field: keyof EditableOrderItem, value: string) => {
    setEditableOrderItems(prev => {
      const updatedItems = [...prev];
      const updatedItem = { ...updatedItems[index] };
      
      updatedItem[field] = value;

      if (field === 'quantity' || field === 'price') {
        const quantity = parseFloat(field === 'quantity' ? value : updatedItem.quantity);
        const price = parseFloat(field === 'price' ? value : updatedItem.price);
        updatedItem.total_amount = (quantity * price).toFixed(2);
      }

      updatedItems[index] = updatedItem;
      return updatedItems;
    });
  }, []);

  const handleReceiptFieldChange = useCallback((field: keyof EditableOrderReceipt, value: string) => {
    setEditableOrderReceipt(prev => prev ? { ...prev, [field]: value } : null);
  }, []);

  const addNewOrderItem = useCallback(() => {
    const newItem: EditableOrderItem = {
      id: `new-${Date.now()}`,
      order_id: editableOrder?.order_id || '',
      product_id: '',
      product_name: '',
      description: '',
      quantity: '1',
      price: '0.00',
      total_amount: '0.00',
      status: 'COMPLETED',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      branch_id: editableOrder?.branch_id || '',
      business_id: editableOrder?.business_id || ''
    };
    setEditableOrderItems(prev => [...prev, newItem]);
  }, [editableOrder]);

  const removeOrderItem = useCallback((index: number) => {
    setEditableOrderItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSaveOrderChanges = async () => {
    if (!editableOrder || !editableOrderReceipt) return;
    
    setSaving(true);
    try {
      const itemsTotal = editableOrderItems.reduce((sum, item) => 
        sum + parseFloat(item.total_amount || '0'), 0
      );

      const amountPaid = parseFloat(editableOrderReceipt.amount_paid || '0');
      const balanceDue = Math.max(0, itemsTotal - amountPaid);

      const saveData = {
        order: {
          ...editableOrder,
          total_price: itemsTotal.toFixed(2),
          payable_amount: itemsTotal.toFixed(2),
          balance_due: balanceDue.toFixed(2),
          amount_paid: amountPaid.toFixed(2),
        },
        order_items: editableOrderItems.map(item => ({
          ...item,
          quantity: item.quantity.toString(),
          price: parseFloat(item.price || '0').toFixed(2),
          total_amount: parseFloat(item.total_amount || '0').toFixed(2),
        })),
        order_receipt: {
          ...editableOrderReceipt,
          total_price: itemsTotal.toFixed(2),
          amount_paid: amountPaid.toFixed(2),
          balance_due: balanceDue.toFixed(2),
        }
      };
      console.log(saveData);
      // Call your API to save changes
      // const response = await updateOrder(saveData);

      // Refresh the report after edit
      if (reportGenerated && startDate && endDate && selectedCustomer) {
        await fetchCustomerReport(selectedCustomer.user_id, startDate, endDate, null);
      }
      
      setEditDialogOpen(false);
      
    } catch (error) {
      console.error('Error saving order:', error);
      alert('Error saving order changes: ' + (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // Edit Order Dialog Component (keep as is)
  const EditOrderDialog = React.memo(({ 
    open, 
    onClose, 
    loading 
  }: { 
    open: boolean;
    onClose: () => void;
    loading: boolean;
  }) => {
    if (!editableOrder || !editableOrderReceipt) return null;

    const itemsTotal = editableOrderItems.reduce((sum, item) => 
      sum + parseFloat(item.total_amount || '0'), 0
    );
    const amountPaid = parseFloat(editableOrderReceipt.amount_paid || '0');
    const balanceDue = Math.max(0, itemsTotal - amountPaid);

    return (
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        sx={{ '& .MuiDialog-paper': { height: '95vh' } }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Edit Order: {editableOrder.order_code}
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ minWidth: 1200 }}>
              {/* Order Details Section */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Order Details
                  </Typography>
                  <Grid container spacing={2}>
                    {/* ... (keep existing edit dialog content) */}
                  </Grid>
                </CardContent>
              </Card>

              {/* Order Items Section */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      Order Items ({editableOrderItems.length})
                    </Typography>
                    <Button startIcon={<Add />} variant="outlined" onClick={addNewOrderItem}>
                      Add New Item
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      {/* ... (keep existing table content) */}
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Order Receipt Section */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Payment Receipt
                  </Typography>
                  <Grid container spacing={2}>
                    {/* ... (keep existing receipt content) */}
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSaveOrderChanges}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

  // Calculate totals for the main table
  const totalPayableAmount = transactions.reduce(
    (sum, txn) => sum + parseFloat(txn.payable_amount || txn.total_price || '0'),
    0
  );

  const unpaidTransactions = transactions.filter(
    (txn) => txn.payment_status === '0' || txn.payment_status === "2"
  );

  const totalBalanceDue = unpaidTransactions.reduce(
    (sum, txn) => sum + (txn.balance_due || 0),
    0
  );

  const selectedTransactionTotal = unpaidTransactions
    .filter((txn) => selectedTransactions.includes(txn.order_id))
    .reduce((sum, txn) => {
      const amount = txn.total_price || txn.payable_amount || "0";
      const balanceDue = txn.balance_due || amount;
      return sum + Number(balanceDue);
    }, 0);

  const totalOutstanding = unpaidTransactions.reduce((sum, txn) => {
    const amount = parseFloat(txn.total_price);
    const balanceDue = txn.balance_due || amount;
    return sum + balanceDue;
  }, 0);

  // NEW: Handle Generate Report Button Click
  const handleGenerateReport = async () => {
    if (!selectedCustomer || !startDate || !endDate) {
      alert("Please select a customer and date range");
      return;
    }
    
    await fetchCustomerReport(selectedCustomer.user_id, startDate, endDate, null);
  };

  return (
    <IonPage className="pushDown">
       <IonHeader>
                <IonToolbar>
                                                  <MenuButton /> {/* This adds the hamburger icon */}
                  
                 </IonToolbar>
              </IonHeader>
      <IonContent>
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Customer Accounts & Payments
          </Typography>

          {/* Customer Search - UNCHANGED */}
          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              placeholder="Search customers by name or phone..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              disabled={!!selectedCustomer}
            />
            {loading.customers && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {searchedCustomers?.length > 0 && (
              <Paper sx={{ mt: 1, maxHeight: 200, overflow: "auto" }}>
                <List>
                  {searchedCustomers?.map((customer, i) => (
                    <ListItem
                      button
                      key={i}
                      onClick={() => handleCustomerSelect(customer)}
                    >
                      <ListItemText
                        primary={`${customer.firstName} ${customer.lastName}`}
                        secondary={`${customer.phone}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          {/* Customer Account Section */}
          {selectedCustomer && (
            <Box>
              {/* Customer Info Header */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ width: 56, height: 56, mr: 2 }}>
                      <Person fontSize="large" />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5">
                        {selectedCustomer.firstName} {selectedCustomer.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedCustomer.phone} • {selectedCustomer.email}
                      </Typography>
                    </Box>
                    <IconButton onClick={() => setSelectedCustomer(null)}>
                      <Close />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>

              {/* NEW: Report Date Selection Section */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Generate Report
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={loading.report}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={loading.report}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleGenerateReport}
                        disabled={!startDate || !endDate || loading.report}
                        startIcon={loading.report ? <CircularProgress size={20} /> : <Receipt />}
                      >
                        {loading.report ? 'Generating...' : 'Generate Report'}
                      </Button>
                    </Grid>
                  </Grid>
                  
                  {/* Next Report Button - Show only after report is generated */}
                  {reportGenerated  && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={handleNextReport}
                        disabled={loading.nextReport}
                        startIcon={loading.nextReport ? <CircularProgress size={20} /> : <NavigateNext />}
                      >
                        {loading.nextReport ? 'Loading...' : `Load More (Page ${currentPage + 1})`}
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Balance and Payment Summary - Only show after report generation */}
              {reportGenerated && transactions.length > 0 && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: balance < 0 ? "error.light" : "success.light" }}>
                      <CardContent sx={{ textAlign: "center" }}>
                        <AccountBalance sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h6">Credit Balance</Typography>
                        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                          {formatCurrencyN(balance)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: "warning.light" }}>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Warning sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h6">Outstanding</Typography>
                        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                          {formatCurrencyN(totalOutstanding)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Receipt sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h6">Total Orders</Typography>
                        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                          {transactions.length}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Orders Table - Only show after report generation */}
              {reportGenerated && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Order History {startDate && endDate && `(${startDate} to ${endDate})`}
                    </Typography>
                    {transactions.length > 0 && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setShowPaymentDialog(true)}
                        disabled={selectedTransactions.length === 0}
                      >
                        Record Payment ({selectedTransactions.length} selected)
                      </Button>
                    )}
                  </Box>

                  {loading.transactions ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : transactions.length > 0 ? (
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectAll}
                                onChange={handleSelectAll}
                                indeterminate={selectedTransactions.length > 0 && selectedTransactions.length < unpaidTransactions.length}
                              />
                            </TableCell>
                            <TableCell>Order Code</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Items</TableCell>
                            <TableCell align="right">Total Amount</TableCell>
                            <TableCell align="right">Balance Due</TableCell>
                            <TableCell>Payment Status</TableCell>
                            <TableCell>Order Status</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {transactions.map((txn) => {
                            const isUnpaid = txn.payment_status === '0' || txn.payment_status === "2";
                            const isSelected = selectedTransactions.includes(txn.order_id);
                            const isExpanded = expandedRows.includes(txn.order_id);

                            return (
                              <React.Fragment key={txn.order_id}>
                                <TableRow sx={{ bgcolor: isSelected ? "action.selected" : "inherit" }}>
                                  <TableCell padding="checkbox">
                                    {isUnpaid && (
                                      <Checkbox
                                        checked={isSelected}
                                        onChange={() => handleTransactionSelect(txn.order_id)}
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                      {txn.order_code}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{txn.order_date}</TableCell>
                                  <TableCell>
                                    <Chip
                                      icon={<ShoppingCart />}
                                      label={`${txn.items.length} items`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                    {formatCurrencyN(parseFloat(txn.payable_amount || txn.total_price))}
                                  </TableCell>
                                  <TableCell align="right" sx={{
                                    color: (txn.balance_due || 0) > 0 ? 'error.main' : 'success.main',
                                    fontWeight: 'bold'
                                  }}>
                                    {formatCurrencyN(Number(txn.balance_due) || 0)}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={getPaymentStatusLabel(txn.payment_status)}
                                      size="small"
                                      color={getPaymentStatusColor(txn.payment_status) as any}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={txn.order_status}
                                      size="small"
                                      color={getOrderStatusColor(txn.order_status) as any}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title="View order items">
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleRowExpansion(txn.order_id)}
                                      >
                                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                      </IconButton>
                                    </Tooltip>
                                    
                                    <Tooltip title="Edit Order">
                                      <IconButton 
                                        size="small" 
                                        onClick={() => handleEditOrder(txn)}
                                        color="primary"
                                      >
                                        <Edit />
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Print receipt">
                                      <IconButton 
                                        size="small" 
                                        onClick={() => handlePrintReceipt(txn)}
                                        color="primary"
                                      > 
                                        <Receipt />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>

                                {/* Expandable row for order items */}
                                <TableRow>
                                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ margin: 1 }}>
                                        <Typography variant="h6" gutterBottom component="div">
                                          Order Items
                                        </Typography>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Product</TableCell>
                                              <TableCell align="right">Quantity</TableCell>
                                              <TableCell align="right">Unit Price</TableCell>
                                              <TableCell align="right">Total</TableCell>
                                              <TableCell>Status</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {txn.items.map((item, index) => (
                                              <TableRow key={`${txn.order_id}-${index}`}>
                                                <TableCell>{item.product_name}</TableCell>
                                                <TableCell align="right">{item.quantity}</TableCell>
                                                <TableCell align="right">
                                                  {formatCurrencyN(parseFloat(item.price))}
                                                </TableCell>
                                                <TableCell align="right">
                                                  {formatCurrencyN(parseFloat(item.total_amount))}
                                                </TableCell>
                                                <TableCell>
                                                  <Chip
                                                    label={item.item_status}
                                                    size="small"
                                                    color={item.item_status === "COMPLETED" ? "success" : "default"}
                                                  />
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                        <TableFooter>
                          <TableRow>
                            <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                              Totals
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                              {formatCurrencyN(totalPayableAmount)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                              {formatCurrencyN(totalBalanceDue)}
                            </TableCell>
                            <TableCell colSpan={3} />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Paper sx={{ p: 3, textAlign: "center" }}>
                      <Typography variant="body1" color="text.secondary">
                        No orders found for this customer in the selected date range
                      </Typography>
                    </Paper>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Payment Dialog */}
          <Dialog open={showPaymentDialog} onClose={() => setShowPaymentDialog(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              Record Payment
              <IconButton sx={{ position: "absolute", right: 8, top: 8 }} onClick={() => setShowPaymentDialog(false)}>
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Customer: {selectedCustomer?.firstName} {selectedCustomer?.lastName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Selected Orders: {selectedTransactions.length}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Total Amount Due: <strong>{formatCurrencyN(selectedTransactionTotal)}</strong>
                  </Typography>

                  <Box sx={{ mt: 2, maxHeight: 200, overflow: "auto" }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Orders:
                    </Typography>
                    {unpaidTransactions
                      .filter((txn) => selectedTransactions.includes(txn.order_id))
                      .map((txn) => (
                        <Box key={txn.order_id} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                          <Typography variant="body2">{txn.order_code}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            {formatCurrencyN(txn.balance_due || 0)}
                          </Typography>
                        </Box>
                      ))}
                  </Box>

                  {paymentAmount > 0 && (
                    <Alert
                      severity={
                        getPaymentType() === "exact"
                          ? "success"
                          : getPaymentType() === "overpayment"
                          ? "info"
                          : "warning"
                      }
                      sx={{ mt: 2 }}
                    >
                      {getPaymentType() === "exact" && "Exact payment - all selected orders will be marked as paid"}
                      {getPaymentType() === "overpayment" &&
                        `Overpayment of ${formatCurrencyN(paymentAmount - selectedTransactionTotal)} - excess will be added to account credit`}
                      {getPaymentType() === "partial" &&
                        `Partial payment - ${formatCurrencyN(selectedTransactionTotal - paymentAmount)} will remain outstanding`}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Payment Amount"
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => handlePaymentAmountChange(parseFloat(e.target.value))}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    }}
                  />

                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <Button size="small" variant="outlined" onClick={() => setPaymentAmount(selectedTransactionTotal)}>
                      Exact Amount
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => setPaymentAmount(selectedTransactionTotal * 0.5)}>
                      50%
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => setPaymentAmount(selectedTransactionTotal * 0.25)}>
                      25%
                    </Button>
                  </Box>

                  <TextField
                    fullWidth
                    select
                    label="Payment Method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="card">POS</MenuItem>
                    <MenuItem value="transfer">Bank Transfer</MenuItem>
                    <MenuItem value="wallet">Wallet</MenuItem>
                  </TextField>

                  <TextField
                    fullWidth
                    label="Payment Notes"
                    multiline
                    rows={3}
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    placeholder="Optional payment notes..."
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handlePaymentSubmit}
                disabled={!paymentAmount || selectedTransactions.length === 0 || loading.payment}
                startIcon={loading.payment ? <CircularProgress size={20} /> : <Receipt />}
              >
                Record Payment ({formatCurrencyN(paymentAmount)})
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        {/* Edit Order Dialog */}
        <EditOrderDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingOrder(null);
            setOrderDetails(null);
            setEditableOrder(null);
            setEditableOrderItems([]);
            setEditableOrderReceipt(null);
          }}
          loading={loadingOrderDetails}
        />

        {/* Receipt Modal */}
        {receiptData && (
          <ReceiptModal
            open={receiptOpen}
            onClose={() => setReceiptOpen(false)}
            receiptData={receiptData}
          />
        )}
      </IonContent>
    </IonPage>
  );
};

export default CustomerAccountPage_account;